// @ts-strict-ignore
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnInit,
  Output,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { LMFormPanelBase } from './lm-form-panel-base';
import { LMFormContentFormComponent } from './lm-form-content-form.component';
import { LMModel } from './model';
import { LMInvalidFieldEvent } from '@field/lm-field-base.component';
import { LMComponentInfoService, LMContextService, LMLoggerService } from '@services/index';
import { Observer, Observable, of } from 'rxjs';
import { LMCommonUtilService } from '@utils/lm-common-util.service';
import { LMCtrlShiftClickBaseComponent, LMRefreshRequest } from '@base/index';

@Component({
  selector: 'lm-form-panel-form',
  standalone: false,
  template: `
    @if (formSpec && initialized === true && !hideLayout) {
      <lm-form-content-form #LMFormStandardComponent
        [formSpec]="formSpec"
        [formOptions]="formOptions"
        [specFields]="specFields"
        [placeHolder]="placeHolder"
        [hideBusyIndicator]="hideBusyIndicator"
        [layout]="layout"
        [model]="model"
        [activatedTabChanged]="activatedTabChanged"
        [isInOverlay]="isInOverlay"
        (fieldEvent)="onFieldEvent($event)"
    ></lm-form-content-form>
  }
    `,
})
export class LMFormPanelFormComponent extends LMCtrlShiftClickBaseComponent implements LMFormPanelBase, OnInit, AfterViewInit {
  @HostBinding('style.display') display = 'block';

  @ViewChild(LMFormContentFormComponent) lmFormContentFormComponent: LMFormContentFormComponent;

  @Input() formSpec: LMFormSpec = null;
  @Input() initialized: boolean;
  @Input() hideBusyIndicator: boolean;
  @Input() fromDataView: LMDataView;
  @Input() formOptions: LMFormOptions;
  @Input() model: LMModel;
  @Input() specFields: LMSpecFields;
  @Input() placeHolder: ViewContainerRef;
  @Input() needsReload: boolean;
  @Input() panelId: string;
  @Input() hasPanelText = false;
  @Input() activatedTabChanged: boolean;
  @Input() hideLayout = false;
  @Input() isInOverlay = false;

  @Input() set activated(activated: boolean) {
    this._activated = activated;
    this.formPanelContentLoaded.emit();
  }

  @Input() set layout(layout: LMFormLayout) {
    this._layout = layout;
    this._bindIds = this.lmCommonUtilService.getBindIdsFromLayout(layout, this.specFields);
  }

  @Output() fieldEvent = new EventEmitter<LMFieldEvent>();
  @Output() formPanelContentLoaded = new EventEmitter<any>();

  private _activated;
  private _layout: LMFormLayout;
  private _bindIds: Array<string> = [];
  readonly instanceId: string;

  constructor(
    private lmLoggerService: LMLoggerService,
    private lmCommonUtilService: LMCommonUtilService,
    protected lmComponentInfoService: LMComponentInfoService,
    private lmContextService: LMContextService,
    private elementRef: ElementRef
  ) {
    super(lmComponentInfoService);
    this.instanceId = this.lmLoggerService.instanceId;
  }

  ngOnInit() {
    this.lmLoggerService.log([ this, this.instanceId, 'ngOnInit()' ]);
  }

  ngAfterViewInit() {
    this.lmLoggerService.log([ this, this.instanceId, 'ngAfterViewInit()' ]);
  }

  public getPanelHeight(): number {
    if (this.elementRef) {
      return this.elementRef.nativeElement.offsetHeight;
    }
    return 0;
  }

  public getComponentInfo(additionalInfo?: LMCompInfoAdditionalInfo): LMComponentInfo {
    const pagingUrls: LMFormPagingUrls = (this.model) ? this.model.getPagingUrls() : undefined;
    const formSpec: LMFormSpec = this.formSpec;

    const panelSpec = this.formSpec.busFormSpec.panels.find(panel => panel.panelId === this.panelId);

    // let resourceInfo: ResourceInfo = formSpec && formSpec.busFormSpec.resourceInfo ? formSpec.busFormSpec.resourceInfo : undefined;
    const compConfigInfo: ConfigInfo = {};
    compConfigInfo['personalization'] = panelSpec ? panelSpec.personalization : undefined;
    compConfigInfo['configuration'] = panelSpec ? panelSpec.configuration : undefined;
    compConfigInfo['configStamp'] = panelSpec ? panelSpec.configStamp : undefined;
    compConfigInfo['classConfigStamp'] = panelSpec ? panelSpec.classConfigStamp : undefined;
    panelSpec['configInfo'] = compConfigInfo;

    const formInfo: LMComponentInfo = {
      componentType: 'FORM_PANEL',
      name: panelSpec ? panelSpec.formName : undefined,
      panelSpec,
      busClassName: formSpec ? formSpec.busClassSpec.name : undefined,

      module: formSpec ? formSpec.busClassSpec.module : undefined,
      type: formSpec ? formSpec.busFormSpec.formType : 'Standard',
      developer: formSpec ? formSpec.developer : undefined,

      lpl: formSpec ? formSpec.busFormSpec.lpl : undefined,

      tenant: this.lmContextService.getContext() ? this.lmContextService.getContext().urlPdlOrDa : undefined,
      hasConfigAccess: this.lmContextService.hasConfigAccess(),
    };

    if (pagingUrls) {
      formInfo.nextRecordUrl = pagingUrls.nextRecordUrl;
      formInfo.previousRecordUrl = pagingUrls.previousRecordUrl;
      formInfo.reloadRecordUrl = pagingUrls.reloadRecordUrl;
    }

    return formInfo;
  }

  onFieldEvent(event) {
    if ('FieldInvalidEvent' === event.eventType || 'BlockingTransactionFieldEvent' === event.eventType) {
      const fieldChangedEvent = event as LMInvalidFieldEvent;
      if (this._bindIds.findIndex(bindId => fieldChangedEvent.bindId === bindId) > -1) {
        fieldChangedEvent.panelId = this.panelId;
      }
    }
    this.fieldEvent.emit(event);
  }

  get activated() {
    return this._activated;
  }

  get layout() {
    return this._layout;
  }

  public refresh(request?: LMRefreshRequest): Observable<boolean> {
    return of (true);
  }

  public save(callBack: Observer<boolean>) {
    // NOTHING TO SAVE
    callBack.next(true);
    callBack.complete();
  }

  public isDirty(): boolean {
    return this.model.isDirty;
  }

  saveCompositeComponentState(): void {
    // noop
  }

  clearCompositeComponentState(): void {
    // noop
  }
}
